import { motion } from "framer-motion";
import { Star, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/lib/api";

const fade = {
  hidden: { opacity: 0, y: 26 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: "easeOut" } }),
};

export const Hero = ({ onBook }) => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center scale-105"
        style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
      />
      {/* readability overlays */}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-barber-bg via-black/40 to-transparent" />

      {/* animated barber-pole accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5 sm:w-2.5 barber-pole z-20" />

      {/* oversized watermark */}
      <span className="hidden lg:block absolute right-6 bottom-6 z-10 font-heading text-[13rem] leading-none text-outline select-none pointer-events-none">
        1905
      </span>

      <div className="relative z-20 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-24">
        <motion.div initial="hidden" animate="show" className="max-w-2xl">
          <motion.div variants={fade} custom={0}
            className="inline-flex items-center gap-3 border border-barber-gold/40 bg-barber-surface/70 backdrop-blur px-4 py-2 mb-8">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#58B033] text-[#58B033]" />
              ))}
            </div>
            <span className="text-barber-cream text-sm">
              <strong className="text-white">4.9</strong>
            </span>
            <span className="w-px h-4 bg-barber-gold/30" />
            <span className="text-barber-muted text-sm">37 Google reviews</span>
          </motion.div>

          <motion.div variants={fade} custom={1} className="flex items-center gap-3 mb-5">
            <span className="h-px w-12 bg-barber-gold" />
            <p className="uppercase tracking-[0.4em] text-barber-gold text-xs">
              Worcester · Classic Cuts
            </p>
          </motion.div>

          <motion.h1 variants={fade} custom={2}
            className="font-heading font-bold text-barber-cream leading-[0.92] tracking-tight text-6xl sm:text-7xl lg:text-8xl">
            The Barber
            <br />
            <span className="italic text-barber-gold">Parlor</span>
          </motion.h1>

          <motion.p variants={fade} custom={3}
            className="mt-8 text-base sm:text-lg text-barber-cream/80 leading-relaxed max-w-xl">
            Precision cuts, hot-towel shaves, and old-school craftsmanship. Book your chair
            in seconds — walk out looking sharp.
          </motion.p>

          <motion.div variants={fade} custom={4} className="mt-9 flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="hero-book-btn"
              onClick={onBook}
              className="rounded-none btn-gold font-semibold text-base px-9 py-6 tracking-wide transition-transform hover:scale-[1.03]"
            >
              Book Appointment
            </Button>
            <a href="tel:+17742322759">
              <Button
                variant="outline"
                className="w-full rounded-none border-barber-gold/40 bg-transparent text-barber-cream hover:bg-barber-gold/10 hover:text-barber-gold text-base px-9 py-6 tracking-wide"
              >
                Call the Shop
              </Button>
            </a>
          </motion.div>

          <motion.div variants={fade} custom={5}
            className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-8 text-sm text-barber-muted">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-barber-gold" />
              170 Prescott St Ste 2, Worcester, MA
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-barber-gold" />
              Open Tue–Sat · Closed Sun &amp; Mon
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 hidden sm:flex flex-col items-center gap-2">
        <span className="text-barber-muted text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="w-px h-8 bg-gradient-to-b from-barber-gold to-transparent animate-pulse" />
      </div>
    </section>
  );
};
